import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API_BASE, api } from "../lib/api";
import { useRunnerModels } from "./TrackDownloads";
import { useAuth } from "../contexts/AuthContext";
import { Sparkles, Square, Loader2, Lock, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

const TOKEN_KEY = "stagvault_token";

/**
 * RunPanel — given a prompt body, streams a GPT-5.2 response via SSE.
 * The user can edit the prompt (so they can fill in placeholders like [TOPIC]) before running.
 */
export function RunPanel({ initialPrompt, idx, slug, promptIndex }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(initialPrompt);
  const models = useRunnerModels();
  const [model, setModel] = useState(() => localStorage.getItem("stagvault_model") || "gpt-5.2");
  const [output, setOutput] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [upsell, setUpsell] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [resending, setResending] = useState(false);
  const [usage, setUsage] = useState(null);
  const abortRef = useRef(null);

  const isPaid = user && typeof user === "object" && (user.tier === "lifetime" || user.tier === "monthly");

  const refreshUsage = async () => {
    if (isPaid) {
      setUsage({ unlimited: true });
      return;
    }
    try {
      const { data } = await api.get("/usage/daily-runs");
      setUsage(data);
    } catch {
      setUsage(null);
    }
  };

  useEffect(() => {
    if (open) refreshUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
    setPrompt(initialPrompt);
    setError("");
    setUpsell(false);
    setVerificationRequired(false);
  };

  const stop = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setRunning(false);
  };

  const run = async () => {
    setError("");
    setUpsell(false);
    setVerificationRequired(false);
    setOutput("");
    setRunning(true);
    const controller = new AbortController();
    abortRef.current = controller;
    trackEvent("PromptRun", { track: slug || null, prompt_index: promptIndex ?? idx ?? null });
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API_BASE}/prompts/run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt,
          category_slug: slug,
          prompt_index: promptIndex,
          model,
        }),
        credentials: "include",
        signal: controller.signal,
      });
      if (res.status === 403) {
        let detail = null;
        try {
          detail = (await res.json()).detail;
        } catch {
          // ignore parse error
        }
        if (detail?.code === "EMAIL_VERIFICATION_REQUIRED") {
          setVerificationRequired(true);
          setError(detail.message || "Verify your email before running prompts with AI.");
          setRunning(false);
          return;
        }
      }
      if (res.status === 402 || res.status === 429) {
        let detail = "";
        try {
          const body = await res.json();
          detail = body.detail || "";
        } catch {
          // ignore parse error
        }
        setUpsell(true);
        setError(detail || (res.status === 429 ? "Daily AI run limit reached." : "This prompt is locked."));
        setRunning(false);
        return;
      }
      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Request failed (${res.status})`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE frames are separated by \n\n
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const frame of frames) {
          if (!frame.trim()) continue;
          const lines = frame.split("\n");
          let eventName = "message";
          let data = "";
          for (const line of lines) {
            if (line.startsWith("event:")) eventName = line.slice(6).trim();
            else if (line.startsWith("data:")) data += line.slice(5).replace(/^ /, "");
          }
          if (eventName === "done") {
            // finished
          } else if (eventName === "error") {
            setError(data || "AI stream error");
          } else if (eventName === "first-run") {
            // Backend tells us this was the user's first successful run, ever.
            toast.success("You just ran your first AI prompt — that's how every track starts. Pick another →", {
              duration: 6000,
            });
          } else if (data) {
            // Decode our \n encoding
            const decoded = data.replace(/\\n/g, "\n");
            setOutput((prev) => prev + decoded);
          }
        }
      }
      refreshUsage();
    } catch (e) {
      if (e.name !== "AbortError") {
        setError(e.message || "Could not run prompt");
      }
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      const { data } = await api.post("/auth/email-verification/resend");
      toast.success(data?.message || "Verification email sent.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send verification email.");
    } finally {
      setResending(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={handleOpen}
        className="inline-flex items-center gap-2 border border-zinc-800 hover:border-purple-500 text-zinc-400 hover:text-purple-300 px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-all duration-300"
        data-testid={`run-prompt-open-${idx}`}
      >
        <Sparkles size={14} />
        Run with AI
      </button>
    );
  }

  return (
    <div className="border-t border-purple-500/20 bg-[#070710]" data-testid={`run-panel-${idx}`}>
      <div className="p-6 md:p-8 space-y-5">
        <div>
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-purple-400">
              <span>· Edit & run ·</span>
              <select value={model} onChange={(e) => { setModel(e.target.value); localStorage.setItem("stagvault_model", e.target.value); }} disabled={running}
                className="bg-black border border-purple-500/40 text-purple-200 text-[10px] uppercase tracking-widest px-2 py-1 focus:outline-none focus:border-purple-400" data-testid={`run-model-select-${idx}`} aria-label="Choose AI model">
                {(models.length ? models : [{ key: "gpt-5.2", label: "ChatGPT · GPT-5.2" }]).map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3">
              {usage && !usage.unlimited && (
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500" data-testid={`run-usage-${idx}`}>
                  {usage.remaining}/{usage.limit} runs left today
                </span>
              )}
              {usage && usage.unlimited && (
                <span className="font-mono text-[10px] uppercase tracking-widest text-purple-400">
                  · Full access · unlimited
                </span>
              )}
              <button
                onClick={() => {
                  stop();
                  setOpen(false);
                  setOutput("");
                  setError("");
                  setUpsell(false);
                  setVerificationRequired(false);
                }}
                className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
                data-testid={`run-panel-close-${idx}`}
              >
                Close ✕
              </button>
            </div>
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={6}
            disabled={running}
            className="w-full bg-black border border-zinc-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none p-4 font-mono text-sm text-zinc-200 resize-y transition-all"
            data-testid={`run-prompt-input-${idx}`}
          />
          <p className="mt-2 font-mono text-[10px] text-zinc-600 tracking-wide">
            Tip: fill in any [PLACEHOLDERS] before running.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {!running ? (
            <button
              onClick={run}
              disabled={!prompt.trim()}
              className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-400 text-black px-6 py-3 font-mono uppercase tracking-[0.25em] text-[11px] transition-all duration-300 shadow-[0_0_20px_rgba(168,85,247,0.35)] hover:shadow-[0_0_35px_rgba(168,85,247,0.7)] disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
              data-testid={`run-prompt-execute-${idx}`}
            >
              <Sparkles size={14} />
              Generate
            </button>
          ) : (
            <button
              onClick={stop}
              className="inline-flex items-center gap-2 border border-red-500/60 text-red-300 hover:bg-red-500/10 px-6 py-3 font-mono uppercase tracking-[0.25em] text-[11px] transition-all duration-300"
              data-testid={`run-prompt-stop-${idx}`}
            >
              <Square size={12} />
              Stop
            </button>
          )}
          {running && (
            <span className="font-mono text-[10px] uppercase tracking-widest text-purple-400 inline-flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" />
              Streaming...
            </span>
          )}
        </div>

        {upsell && (
          <div
            className="border border-purple-500/40 bg-purple-500/[0.08] px-4 py-4 flex items-start gap-3 flex-wrap"
            data-testid={`run-prompt-upsell-${idx}`}
          >
            <Lock size={16} className="text-purple-300 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-purple-200 text-sm">{error}</div>
              <div className="text-zinc-400 text-xs mt-1">Lifetime Access: unlimited runs on ChatGPT, Claude or Gemini · £25 once, no subscription required</div>
            </div>
            <Link
              to="/pricing"
              className="bg-purple-500 hover:bg-purple-400 text-black px-4 py-2 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300"
              data-testid={`run-prompt-upsell-cta-${idx}`}
            >
              Upgrade →
            </Link>
          </div>
        )}

        {verificationRequired && (
          <div className="border border-amber-400/40 bg-amber-400/[0.07] px-4 py-4 flex items-start gap-3 flex-wrap" data-testid={`run-verification-required-${idx}`}>
            <MailCheck size={17} className="text-amber-300 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-amber-100 text-sm" data-testid={`run-verification-message-${idx}`}>{error}</div>
              <div className="text-zinc-400 text-xs mt-1">We sent a single-use verification link to {user?.email}.</div>
            </div>
            <button
              type="button"
              onClick={resendVerification}
              disabled={resending}
              className="border border-amber-300/50 hover:bg-amber-300 hover:text-black text-amber-200 px-4 py-2 text-xs tracking-[0.2em] uppercase font-bold transition-colors disabled:opacity-50"
              data-testid={`run-verification-resend-${idx}`}
            >
              {resending ? "Sending…" : "Resend email"}
            </button>
          </div>
        )}

        {error && !upsell && !verificationRequired && (
          <div
            className="border border-red-500/40 bg-red-500/10 text-red-300 px-4 py-3 font-mono text-xs"
            data-testid={`run-prompt-error-${idx}`}
          >
            {error}
          </div>
        )}

        {(output || running) && (
          <div data-testid={`run-prompt-output-${idx}`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              · AI response ·
            </div>
            <div className="bg-black border border-purple-500/30 p-5 md:p-6 font-mono text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed min-h-[120px]">
              {output}
              {running && <span className="inline-block w-2 h-4 bg-purple-400 ml-0.5 animate-pulse align-text-bottom" />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
